import { atom, useAtom } from "jotai";
import { useEffect, useState } from "react";

// Theme preference. `null` means "follow OS"; explicit values pin the choice.
// Persisted to localStorage by the pre-paint inline script in index.html
// (so the very first paint has the right class) and by useThemeToggle on
// subsequent clicks.
export type ThemePreference = "light" | "dark" | null;

const STORAGE_KEY = "plus.theme";

const readInitial = (): ThemePreference => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }
  } catch {
    // localStorage may be disabled (Safari private, etc.) — fall through
  }
  return null;
};

export const themeAtom = atom<ThemePreference>(readInitial());

// Resolve a preference (which may be null = "follow OS") to a concrete
// light/dark mode.
const resolveEffective = (pref: ThemePreference): "light" | "dark" => {
  if (pref !== null) {
    return pref;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

// Returns the effective theme + a stable toggle. `mounted` is false during
// the first render, true after mount — callers gate icon-rendering on it so
// the icon matches what the pre-paint script applied.
export const useThemeToggle = (): {
  mounted: boolean;
  effective: "light" | "dark";
  toggle: () => void;
} => {
  const [pref, setPref] = useAtom(themeAtom);
  const [effective, setEffective] = useState<"light" | "dark">(() => resolveEffective(pref));
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const next = resolveEffective(pref);
    document.documentElement.classList.toggle("dark", next === "dark");
    setEffective(next);
    if (pref !== null) {
      try {
        window.localStorage.setItem(STORAGE_KEY, pref);
      } catch {
        // persistence is best-effort; toggling still works in-session
      }
    }
  }, [pref]);

  const toggle = () => {
    setPref(effective === "dark" ? "light" : "dark");
  };

  return { mounted, effective, toggle };
};
